import dayjs from "dayjs";
import { handleActions } from "redux-actions";
import {
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_TABLE_CHART_SUCCESS,
  FETCH_LINE_CHART_SUCCESS,
  FETCH_FILTERS_SUCCESS,
  UPDATE_CROSS_REGIONAL_VISITS_SELECTED,
  UPDATE_CUSTOM_RANGE,
  UPDATE_PERIOD_TYPE,
  UPDATE_DATES,
  SET_CROSS_REGIONAL_VISITS,
  SET_UPDATES_LOADING,
  SET_FILTERS_DIRTY,
  SET_LOADING,
  SET_DATES,
  RESET_FILTER_SELECTED,
  UPDATE_PROSPECT_TYPE,
  UPDATE_MEMBERSHIP_TYPE,
  UPDATE_FILTER_BY
} from "./activeClientsSagas";
import { PROSPECT_TYPES } from "../constants";
import {
  getDateRanges,
  getMonthlyRangeOptions,
  getRangeOptions,
  getRangeOptionsWithCustom,
  getSelectedRangeOptionWithCustom
} from "../../../../utils/date-range-options";
import { API_DATE_FORMAT } from "../../../../app_constants";

export const initialState = {
  currentUser: {},
  initialRequest: false,
  chartData: {},
  tableChartData: {},
  isLoading: true,
  updatesLoading: false,
  filtersDirty: false,
  selectedRequestDates: {},
  filters: {
    prospect_types: [],
    membership_types: [],
    period_type: "monthly",
    toggles: {
      crossRegionalVisits: {},
      compareDates: {}
    }
  },
  selectedFilters: {
    period_type: "Monthly",
    include_cross_regional_visits: false,
    prospect_types: [],
    membership_types: [],
    filter_by: PROSPECT_TYPES
  },
  periodTypeOptions: ["Daily", "Monthly"],
  selectedRangeOption: {},
  rangeOptions: [],
  monthlyRangeOptions: [],
  dailyRangeOptions: []
};

export default handleActions(
  {
    [SET_LOADING]: (state, action) => ({
      ...state,
      isLoading: action.payload
    }),
    [SET_UPDATES_LOADING]: (state, action) => ({
      ...state,
      updatesLoading: action.payload
    }),
    [SET_FILTERS_DIRTY]: (state, action) => ({
      ...state,
      filtersDirty: action.payload
    }),
    [SET_DATES]: (state, action) => {
      const { endDate } = action.payload;
      const { calendarStartDate } = state.currentUser;
      const monthlyRangeOptions = getMonthlyRangeOptions(endDate, calendarStartDate);

      return {
        ...state,
        selectedRequestDates: action.payload,
        monthlyRangeOptions,
        dailyRangeOptions: getRangeOptions(endDate, calendarStartDate),
        rangeOptions: getRangeOptionsWithCustom(monthlyRangeOptions, action),
        selectedRangeOption: getSelectedRangeOptionWithCustom(action)
      };
    },
    [SET_CROSS_REGIONAL_VISITS]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        include_cross_regional_visits: action.payload
      }
    }),
    [UPDATE_CUSTOM_RANGE]: (state, action) => ({
      ...state,
      selectedRangeOption: action.payload,
      selectedRequestDates: getDateRanges(
        state.currentUser.calendarEndDate,
        action.payload,
        state.currentUser.calendarStartDate
      ),
      filtersDirty: true
    }),
    [UPDATE_DATES]: (state, action) => ({
      ...state,
      selectedRequestDates: action.payload,
      filtersDirty: true,
      rangeOptions: getRangeOptionsWithCustom(state.rangeOptions, action),
      selectedRangeOption: getSelectedRangeOptionWithCustom(action)
    }),
    [FETCH_CURRENT_USER_SUCCESS]: (state, action) => ({
      ...state,
      currentUser: action.payload,
      initialRequest: true
    }),
    [FETCH_FILTERS_SUCCESS]: (state, action) => ({
      ...state,
      filters: action.payload || state.filters,
      selectedFilters: {
        ...state.selectedFilters,
        prospect_types: action.payload.prospect_types,
        membership_types: action.payload.membership_types
      }
    }),
    [FETCH_LINE_CHART_SUCCESS]: (state, action) => ({
      ...state,
      chartData: action.payload
    }),
    [FETCH_TABLE_CHART_SUCCESS]: (state, action) => ({
      ...state,
      tableChartData: action.payload
    }),
    [RESET_FILTER_SELECTED]: (state) => ({
      ...state,
      filtersDirty: false,
      selectedFilters: {
        ...initialState.selectedFilters,
        prospect_types: state.filters.prospect_types,
        membership_types: state.filters.membership_types
      }
    }),
    [UPDATE_CROSS_REGIONAL_VISITS_SELECTED]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        include_cross_regional_visits: action.payload
      }
    }),
    [UPDATE_PROSPECT_TYPE]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        prospect_types: action.payload
      }
    }),
    [UPDATE_MEMBERSHIP_TYPE]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        membership_types: action.payload
      }
    }),
    [UPDATE_FILTER_BY]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        filter_by: action.payload
      }
    }),
    [UPDATE_PERIOD_TYPE]: (state, action) => {
      const isMonthly = action.payload === "Monthly";

      const baseRangeOptions = isMonthly ? state.monthlyRangeOptions : state.dailyRangeOptions;

      const datesForCustomRange = isMonthly
        ? getDatesForCustomRange({
            selectedRangeOption: state.selectedRangeOption,
            selectedRequestDates: state.selectedRequestDates,
            calendarEndDate: state.currentUser.calendarEndDate
          })
        : {};

      return {
        ...state,
        filtersDirty: true,
        ...datesForCustomRange,
        rangeOptions: getRangeOptionsWithCustom(baseRangeOptions, {
          payload: datesForCustomRange.selectedRequestDates || state.selectedRequestDates
        }),
        selectedFilters: {
          ...state.selectedFilters,
          period_type: action.payload
        }
      };
    }
  },
  initialState
);

const getDatesForCustomRange = ({
  selectedRangeOption: { name, startDate, endDate },
  selectedRequestDates,
  calendarEndDate
}) => {
  const isLast30Days = name?.includes("Last 30 Days");
  const isCustomRange = name?.includes("Custom Range");

  if (isCustomRange || isLast30Days) {
    const fromDate = isCustomRange ? startDate : selectedRequestDates.startDate;
    const toDate = isCustomRange ? endDate : selectedRequestDates.endDate;

    let newStartDate = dayjs(fromDate).startOf("month").format(API_DATE_FORMAT);
    let newEndDate = dayjs(toDate).isSame(calendarEndDate, "month")
      ? calendarEndDate
      : dayjs(toDate).endOf("month").format(API_DATE_FORMAT);

    let payload = {
      startDate: newStartDate,
      endDate: newEndDate
    };
    return {
      selectedRequestDates: payload,
      selectedRangeOption: getSelectedRangeOptionWithCustom({ payload })
    };
  }
  return {};
};
